import { valibotResolver } from '@hookform/resolvers/valibot'
import { useRouter } from '@tanstack/react-router'
import { type RcFile } from 'antd/es/upload'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, type FC } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaCheckCircle } from 'react-icons/fa'

import { Button, ModalBody, ModalFooter } from '@/components/elements'
import {
  ACTIONS,
  DESCRIPTIONS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'
import { createShipmentOrderImportJob } from '@/features/shipment-order-import-jobs/api'
import {
  type ShipmentOrderImportFormValue,
  shipmentOrderImportFormValueSchema
} from '@/features/shipment-order-import-jobs/models'
import { FileUploadDragger } from '@/features/uploads/components'
import { type UploadFileResult } from '@/features/uploads/models'
import { validateCsvFile } from '@/utils/upload-file-validator'

import { selectedDcIdAtom } from '../../-states'

type Props = {
  tenantKey: string
  onSuccess: () => Promise<void>
  onCancel: () => void
}

export const ShipmentOrderImportModalContent: FC<Props> = ({
  tenantKey,
  onSuccess,
  onCancel
}) => {
  const router = useRouter()

  const {
    setValue,
    handleSubmit,
    formState: { isSubmitting, isValid }
  } = useForm<ShipmentOrderImportFormValue>({
    values: {
      fileKey: '',
      fileName: ''
    },
    resolver: valibotResolver(shipmentOrderImportFormValueSchema)
  })

  const isValidUploadFile = useCallback((file: RcFile) => {
    return validateCsvFile(file)
  }, [])

  const handleChangeUploadFile = useCallback(
    (result: UploadFileResult | null) => {
      setValue('fileKey', result?.key ?? '', { shouldValidate: true })
      setValue('fileName', result?.name ?? '', { shouldValidate: true })
    },
    [setValue]
  )

  const selectedDcId = useAtomValue(selectedDcIdAtom)

  const onSubmit = useCallback(
    async (formValue: ShipmentOrderImportFormValue) => {
      try {
        await createShipmentOrderImportJob({
          tenantKey,
          dto: { ...formValue, dcId: selectedDcId }
        })

        onSuccess()
        toast.success(SUCCESS_MESSAGES.STARTED_IMPORTING_SHIPMENT_ORDER)

        router.navigate({
          to: '/$tenantKey/shipment-order-import-jobs',
          params: { tenantKey }
        })
      } catch (error) {
        toastApiErrorMessage(
          error,
          ERROR_MESSAGES.FAILED_TO_IMPORT_SHIPMENT_ORDER
        )
      }
    },
    [tenantKey, onSuccess, router, selectedDcId]
  )

  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', () => {
      onCancel()
    })
    return () => {
      unsubscribe()
    }
  }, [router, onCancel])

  return (
    <form
      onSubmit={handleSubmit(onSubmit, (error) => console.log('error', error))}
    >
      <ModalBody>
        <FileUploadDragger
          title={ACTIONS.UPLOAD_SHIPMENT_ORDER}
          description={
            <>
              {DESCRIPTIONS.CSV_FILE_UPLOAD}
              <br />
              {DESCRIPTIONS.PLEASE}
            </>
          }
          isValidFile={isValidUploadFile}
          onChangeUploadFile={handleChangeUploadFile}
        />
      </ModalBody>
      <ModalFooter className="!justify-start">
        <div className="flex w-full items-center gap-2"></div>
        <Button onClick={onCancel}>{ACTIONS.CANCEL}</Button>
        <Button
          type="primary"
          icon={<FaCheckCircle />}
          htmlType="submit"
          disabled={!isValid}
          loading={isSubmitting}
        >
          {ACTIONS.IMPORT}
        </Button>
      </ModalFooter>
    </form>
  )
}
